/* **The app's one tab strip** — the scroller, the tab, the selected tab's three channels, and the
 * ember rule that opens under it.
 *
 * Promoted out of `pages/client-detail/ui/ClientDetailScreen.module.css` on 17 September 2026, on
 * its second consumer (`/raporty`). Every declaration below was that stylesheet's, moved unchanged
 * — except the rule's own treatment, which moved on again to `shared/ui/tabRule.module.css` on 18
 * September 2026 when the owner asked for ONE underline across every bar in the app. `.rule` here
 * is now the offset and nothing else.
 *
 * ── What is here, and what the surface declares — `ghostButton`'s contract ───────────────────
 * This module owns the TAB: its geometry, its ink, its weights, its focus ring and its rule. The
 * surface owns the BAR's box — `height`, `padding`, `gap`, `background`, `border-bottom` — because
 * where a strip stands is a fact about the screen and not about the strip. The client detail draws
 * it as a 46px band on `--bg-card` under the client's name; `/raporty` draws it on the page's own
 * ground under the heading.
 *
 * **Nothing may be declared on both sides.** A declaration living in a shared module and a page's
 * own is settled by CHUNK ORDER, which differs between the dev server and the build — the argument
 * is written out at length in `emberButton.module.css`, and this repo has already paid for it once.
 * That is also why the tab's geometry is HERE rather than handed over: `.tabOn` raises the weight
 * and a surface restating the resting `font-weight` would be two single-class selectors in two
 * files racing for one property.
 *
 * ── One geometry, and it is the app's rather than a per-screen reading of KANON ──────────────
 * 13px at 600, rising to 700, in a 45px tab. KANON `Raporty` writes 13.5px at 700 on BOTH states;
 * taking that literally would erase the weight channel, which is the thing that keeps the selected
 * tab legible in greyscale. Recorded here so the half-pixel is read as a decision.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.bar {
  display: flex;
  flex: none;
  align-items: center;
}

/* The strip scrolls sideways rather than wrapping. Six tabs fit at 1280 and a seventh would not; a
 * wrapped strip changes the height of the chrome and pushes the panel down.
 *
 * ── The vertical axis is OFF, and the pixel that turned it on is gone ────────────────────────
 * `overflow-x: auto` on its own does not leave the other axis alone: a `visible` axis paired with
 * a non-visible one computes to `auto`, so this box was scrollable in BOTH directions. It then
 * found one pixel to scroll: `.rule` sits at `bottom: -1px` so the selected tab's accent rule
 * covers the bar's hairline instead of stacking above it, and an absolutely positioned descendant
 * a pixel below its parent enters this box's scrollable overflow. 45px of content, 46px of
 * scroll — enough for a full-height vertical scrollbar down the right of the tab strip.
 *
 * Both halves are fixed rather than the visible one. `overflow-y: hidden` states the axis instead
 * of leaving it to the pairing rule, and the padding/negative-margin pair makes this box RESERVE
 * the pixel its own rule draws into: the padding puts that pixel inside the scrollport, the equal
 * negative margin takes it back out of the layout, so the bar's height and the rule's position on
 * the hairline are both exactly what they were. */
.tabs {
  display: flex;
  flex: 1;
  gap: var(--space-6);
  align-items: center;
  min-width: 0;
  padding-block-end: var(--border-hairline);
  margin-block-end: calc(var(--border-hairline) * -1);

  /* x then y. The shorthand rather than the pair because stylelint collapses the two longhands on
   * its next `--fix` run anyway, and a rule that is rewritten behind you is a rule nobody trusts. */
  overflow: auto hidden;
}

.tab {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-detail-tab);
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
  cursor: pointer;
  background: none;
  border: none;
  transition: color var(--dur-fast) var(--ease-screen-entrance);
}

.tab:hover {
  color: var(--text-2);
}

/* The selected tab is a WORD at a heavier weight AND a darker ink AND an underline. Three channels,
 * because `aria-selected` is not visible and an underline alone disappears in greyscale. */

/* THINNED 2 September 2026, and it is a decision. KANON writes 800 here; this app writes 700, and
 * the unselected tab stays at 600, so the weight channel above survives. ONE step, not the two the
 * rest of the pass took, and deliberately so: this word sits nearest the heading layer the owner
 * exempted. Do not take it to 600 "for hierarchy" — that would erase the channel. The record is
 * `src/pages/client-detail/AGENTS.md`. */
.on {
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* ── The rule itself is `shared/ui/tabRule.module.css`, and only its OFFSET is here ───────────
 * The fill, the height, the resting `scaleX(0)` and the transition that opens it are the app's one
 * tab underline, worn by the roster's filter tabs and both scope switches as well. What is left to
 * this strip is where the rule sits: ON the bar's own hairline (`--offset-tab-rule`), or the row
 * reads as two rules a pixel apart.
 *
 * Nothing else about the rule may be written here — see that file's header for the chunk-order
 * argument, and `.tabs` above for the pixel this negative offset costs the scrollport. */
.rule {
  bottom: var(--offset-tab-rule);
}

.tab:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-1px);
}

/* ── The `cards` skin — the Talia's tabs (KANON "Karty zadań", 24 September 2026) ─────────────
 * `canvas.css` `.t2-tab`: equal boxes sharing the row, a hairline edge at rest, and the chosen one
 * on the card's own ground with a stronger edge. The weight and ink channels of the rule skin stay:
 * the chosen tab's words go to `--text`, so it is legible in greyscale without its edge. */
.cardTabs {
  flex-wrap: wrap;
  align-items: stretch;
  gap: var(--space-2);
  padding-block-end: var(--space-0);
  margin-block-end: var(--space-0);
}

.cardTab {
  display: flex;

  /* Equal boxes in one row while they fit; a client with many open tasks wraps onto a second row
   * rather than squeezing every kind to an ellipsis. */
  flex: 1 1 var(--w-card-tab-min);
  align-items: center;
  min-width: 0;
  padding: var(--space-9px) var(--space-3);
  font-family: var(--font-body);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.cardTab:hover {
  border-color: var(--border-2);
}

.cardOn {
  color: var(--text);
  background: var(--bg-card);
  border-color: var(--text-2);
}

.cardTab:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-1px);
}
